import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useState, useRef, useEffect } from 'react';
import { useIntersectionObserver } from './helpers/use-intersection-observer.esm.js';
import { useWistiaVideoApi, initWistiaVideo } from './helpers/use-wistia.esm.js';

const KEYBOARD_KEYS = {
    SPACE: ' ',
    ENTER: 'Enter'
};
const Video = ({ thumbnail, thumbnailAlt, rootRef, id, containerId, controls, togglePlay, time, options, onLoad })=>{
    const [isThumbnailVisible, setIsThumbnailVisible] = useState(!!thumbnail);
    const [isControlsVisible, setIsControlsVisible] = useState(controls);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasPlayed, setHasPlayed] = useState(false);
    const videoId = id || containerId;
    const wistiaRef = useRef(null);
    const thumbnailRef = useRef(null);
    const [containerRef, setContainerRef] = useState(null);
    const container = useIntersectionObserver(containerRef, {
        freezeOnceVisible: true,
        rootMargin: '300px 0px 300px 0px'
    });
    const isVisible = !!container?.isIntersecting;
    useEffect(()=>{
        if (controls !== isControlsVisible) {
            setIsControlsVisible(controls);
            setIsInitialized(false);
        }
    }, [
        controls
    ]);
    useEffect(()=>{
        if (rootRef?.current) {
            setContainerRef(rootRef);
            return;
        }
        if (thumbnailRef?.current) {
            setContainerRef(thumbnailRef);
            return;
        }
        if (wistiaRef?.current) {
            setContainerRef(wistiaRef);
            return;
        }
    }, [
        rootRef,
        thumbnailRef,
        wistiaRef
    ]);
    useEffect(()=>{
        if (wistiaRef.current) {
            wistiaRef.current.style.height = '';
            if (!isControlsVisible && !thumbnail) {
                wistiaRef.current.style.pointerEvents = 'none';
            }
        }
    }, [
        wistiaRef,
        isControlsVisible,
        thumbnail
    ]);
    const videoApi = useWistiaVideoApi({
        id: videoId || '',
        jsonp: true,
        options
    });
    useEffect(()=>{
        if (isVisible && !isInitialized && !isThumbnailVisible) {
            prepareVideo();
        }
    }, [
        isVisible,
        isInitialized,
        isThumbnailVisible
    ]);
    useEffect(()=>{
        if (isVisible && !isThumbnailVisible && videoApi) {
            videoApi?.play();
        }
        return ()=>{
            videoApi?.remove();
        };
    }, [
        isVisible,
        isThumbnailVisible,
        videoApi
    ]);
    useEffect(()=>{
        if (!videoApi || isThumbnailVisible) {
            return;
        }
        if (typeof time !== 'undefined') {
            videoApi.time(time);
        }
        if (typeof togglePlay !== 'undefined' && togglePlay) {
            videoApi.play();
        }
        if (typeof togglePlay !== 'undefined' && !togglePlay) {
            videoApi.pause();
        }
    }, [
        togglePlay,
        time,
        videoApi,
        isThumbnailVisible
    ]);
    /**
     * Wistia's API report onLoad event before video is mounted
     * onLoad event is triggered instead when video is mounted
     * and starts playing
     */ useEffect(()=>{
        if (hasPlayed) {
            return;
        }
        const handleStartPlaying = ()=>{
            setHasPlayed(true);
            onLoad?.();
        };
        videoApi?.bind('play', handleStartPlaying);
        return ()=>{
            videoApi?.unbind('play', handleStartPlaying);
        };
    }, [
        videoApi,
        hasPlayed,
        onLoad
    ]);
    const prepareVideo = ()=>{
        const classes = [];
        if (!isControlsVisible) {
            classes.push('settingsControl=false', 'controlsVisibleOnLoad=false', 'volumeControl=false', 'playButton=false', 'playbar=false', 'smallPlayButton=false', 'qualityControl=false', 'fullscreenButton=false', 'playbackRateControl=false', 'muted=true');
        }
        setIsInitialized(true);
        if (wistiaRef.current) {
            initWistiaVideo({
                wrapper: wistiaRef.current,
                embedId: containerId,
                autoPlay: true,
                classes,
                videoId
            });
        }
    };
    const handleClickThumbnail = ()=>{
        /**
         * Prevent content from shifting while thumbnail disappears and video appears
         */ if (wistiaRef.current) {
            wistiaRef.current.style.height = `${thumbnailRef.current?.offsetHeight}px`;
        }
        if (isThumbnailVisible) {
            prepareVideo();
        }
        setIsThumbnailVisible(false);
    };
    const handleKeyPress = (event)=>{
        if (event.key == KEYBOARD_KEYS.ENTER || event.key == KEYBOARD_KEYS.SPACE) {
            event.preventDefault();
            handleClickThumbnail();
        }
    };
    const getThumbnail = ()=>{
        return jsx("div", {
            className: "wistia-thumbnail-wrapper",
            ref: thumbnailRef,
            tabIndex: 0,
            role: "button",
            onClick: handleClickThumbnail,
            onKeyUp: handleKeyPress,
            children: jsxs("picture", {
                children: [
                    jsx("source", {
                        srcSet: `https://embed-fastly.wistia.com/deliveries/${thumbnail}.webp?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0&image_resize=480`,
                        type: "image/webp",
                        media: "(max-width: 480px)"
                    }),
                    jsx("source", {
                        srcSet: `https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0&image_resize=480`,
                        type: "image/jpeg",
                        media: "(max-width: 480px)"
                    }),
                    jsx("source", {
                        srcSet: `https://embed-fastly.wistia.com/deliveries/${thumbnail}.webp?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`,
                        type: "image/webp"
                    }),
                    jsx("source", {
                        srcSet: `https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`,
                        type: "image/jpeg"
                    }),
                    jsx("img", {
                        src: `https://embed-fastly.wistia.com/deliveries/${thumbnail}.jpg?image_play_button_size=2x&image_play_button=1&image_play_button_color=050038e0`,
                        alt: thumbnailAlt
                    })
                ]
            })
        });
    };
    return jsxs(Fragment, {
        children: [
            isThumbnailVisible && getThumbnail(),
            jsx("div", {
                ref: wistiaRef,
                "data-testid": "wistia-element-ref"
            }, JSON.stringify({
                controls
            }))
        ]
    });
};

export { Video };
